import { Observable, of } from "rxjs";
import { ObjectUtils } from "../utils/object-utils";
import { IValidationContext } from "./IValidationContext";
import { IValidationRule } from "./IValidationRule";
import { IValidationScope } from "./IValidationScope";
import { IValidationScopeBuilder } from "./IValidationScopeBuilder";
import { ValidationErrorLevels } from "./ValidationErrorLevels";
import { ValidationScope } from "./ValidationScope";

export type StringFunc = () => string;

export class ValidationContext implements IValidationContext {

    current: IValidationScope;
    scopes: { [scopeName: string]: ValidationScope } = {};
    errorLevel: string = ValidationErrorLevels.warning;

    get isWarning(): boolean {
        return this.errorLevel === ValidationErrorLevels.warning;
    }

    get errorRules(): IValidationRule[] {
        if (this.isWarning) {
            return [];
        }
        return this.current.rules
            .reduce((aggregate, rule) => {
                if (rule.hasError && rule.error && aggregate.map(r => r.error).indexOf(rule.error) < 0) {
                    aggregate.push(rule);
                }

                return aggregate;
            },
            []);
    }

    get errors(): string[] {
        const errors = this.errorRules;
        if (errors.length > 0) {
            return this.errorRules.map(rule => rule.error);
        } else {
            return [];
        }
    }

    hasErrors = (scopeName: string): boolean => {
        if (!this.current || this.current.name !== scopeName) {
            return false;
        }
        const currentErrors = this.errors;

        return currentErrors ? currentErrors.length > 0 : false;
    };

    scope = (name: string, errorLevel?: string): Observable<IValidationScope> => {
        if (errorLevel !== undefined) {
            this.errorLevel = errorLevel;
        }
        this.current = this.scopes[name];
        return of(this.current);
    };

    validate = (verbose = false): boolean => {
        this.current.rules.forEach(rule => {
            rule.validateModel(verbose);
            // Om errorLevel ändras måste vyn valideras om
            rule.validateView();
        });
        return !this.current.rules.some(x => x.hasError);
    };

    create = (name: string, errorLevel?: string): IValidationScopeBuilder => {
        this.errorLevel = ObjectUtils.hasValue(errorLevel) ? errorLevel : ValidationErrorLevels.warning;
        const scope = new ValidationScope(name, () => this);
        this.scopes[name] = scope;
        this.setDefaultIfFirst();
        return scope;
    };

    private setDefaultIfFirst() {
        const keys = Object.keys(this.scopes);
        if (keys.length === 1) {
            this.current = this.scopes[keys[0]];
        }
    }
}
